import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { tenderAwardeeService, TenderAwardeeDetail } from '../services/tenderAwardeeService';
import { TenderFilters } from '../services/tenderService';
import { FiPhone, FiCalendar, FiMail, FiMapPin, FiMessageSquare, FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import ClickToCallButton from '../components/calls/ClickToCallButton';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { getSettingColorClass } from '../utils/settingColors';
import { usePermissions } from '../hooks/usePermissions';
import { useTenderEditModal } from '../hooks/useTenderEditModal';
import { Tender } from '../types';

type AwardeeNavState = {
  highlightTenderId?: number;
  listRestore?: { page: number; perPage: number; filters: TenderFilters };
};

const TenderAwardeeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as AwardeeNavState | null) ?? {};
  const highlightTenderId = navState.highlightTenderId ?? null;
  const listRestore = navState.listRestore;

  const { hasPermission } = usePermissions();
  const canEditTender = hasPermission('tenders', 'edit');
  const [awardee, setAwardee] = useState<TenderAwardeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { openEdit, editModal } = useTenderEditModal(() => loadAwardee());

  useEffect(() => {
    if (id) {
      loadAwardee();
    }
  }, [id]);

  const loadAwardee = async () => {
    try {
      setLoading(true);
      const data = await tenderAwardeeService.getDetail(parseInt(id || '0', 10));
      setAwardee(data);
      setError('');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Failed to load awardee');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value == null || value === 0) return '—';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested':
        return 'bg-green-100 text-green-800';
      case 'not_interested':
        return 'bg-red-100 text-red-800';
      case 'callback':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const backToTenders = () => {
    if (listRestore) {
      navigate('/tenders', { state: { restore: listRestore } });
    } else {
      navigate('/tenders');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8 text-sm text-gray-600">Loading...</div>
      </Layout>
    );
  }

  if (!awardee) {
    return (
      <Layout>
        <div className="text-center py-8 text-sm text-red-600">{error || 'Awardee not found'}</div>
      </Layout>
    );
  }

  const stats = awardee.call_statistics;
  const tenders = awardee.tenders || [];
  const callLogs = awardee.call_logs || [];

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-5">
        <div>
          <button
            type="button"
            onClick={backToTenders}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 mb-3"
          >
            <Icon component={FiArrowLeft} className="w-4 h-4" />
            <span className="text-sm">Back to Tenders</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Awardee profile */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{awardee.name}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {awardee.awardee_city && (
              <div className="flex items-center gap-1 text-gray-700">
                <Icon component={FiMapPin} className="w-4 h-4 text-gray-400" />
                <span>{awardee.awardee_city}</span>
              </div>
            )}
            {awardee.awardee_phone && (
              <div className="flex items-center gap-2">
                <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{awardee.awardee_phone}</span>
                <ClickToCallButton
                  entityType="tender"
                  entityId={tenders[0]?.id ?? 0}
                  entityName={awardee.name}
                  phoneNumber={awardee.awardee_phone}
                />
              </div>
            )}
            {awardee.awardee_email && (
              <div className="flex items-center gap-1 text-gray-700 min-w-0">
                <Icon component={FiMail} className="w-4 h-4 text-gray-400 shrink-0" />
                <a href={`mailto:${awardee.awardee_email}`} className="text-yellow-700 hover:underline truncate">
                  {awardee.awardee_email}
                </a>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{awardee.tender_count ?? tenders.length}</div>
              <div className="text-xs text-gray-600 mt-1">Tenders</div>
            </div>
            <div className="text-center p-3 bg-indigo-50 rounded-lg">
              <div className="text-lg font-bold text-indigo-600">
                {formatCurrency(awardee.summary?.total_work_order_value)}
              </div>
              <div className="text-xs text-gray-600 mt-1">Total work order</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {awardee.summary?.converted_tender_count ?? 0}
              </div>
              <div className="text-xs text-gray-600 mt-1">Converted tenders</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats?.total_calls ?? 0}</div>
              <div className="text-xs text-gray-600 mt-1">Total calls</div>
            </div>
          </div>
        </div>

        {/* Call statistics */}
        {stats && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Statistics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.total_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Total Calls</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.answered_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Answered</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{stats.no_answer_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">No Answer</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{stats.interested_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Interested</div>
              </div>
            </div>
            {stats.last_call_date && (
              <p className="mt-3 text-sm text-gray-600">
                Last call: {formatDate(stats.last_call_date)}
              </p>
            )}
          </div>
        )}

        {/* Tenders history */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Tenders History</h2>
          <p className="text-sm text-gray-600 mb-4">
            {tenders.length === 0
              ? 'No tenders on record for this awardee.'
              : `${tenders.length} tender${tenders.length === 1 ? '' : 's'} awarded to ${awardee.name}`}
          </p>
          {tenders.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Awarder</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">City</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Brand</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Work order</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700"> </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tenders.map((t) => {
                    const isCurrent = highlightTenderId != null && t.id === highlightTenderId;
                    return (
                      <tr
                        key={t.id}
                        className={isCurrent ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50'}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate" title={t.awarder || ''}>
                          {t.awarder || '—'}
                        </td>
                        <td className="px-3 py-2">{t.city || t.awardee_city || '—'}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {t.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{t.brand_name || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {t.work_order_value != null ? formatCurrency(t.work_order_value) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {isCurrent && (
                              <span className="text-xs font-semibold text-yellow-800">Viewing</span>
                            )}
                            <Link
                              to={`/tenders/${t.id}`}
                              state={{
                                assignmentId: t.assignment_id,
                                brandId: t.brand_id ?? undefined,
                                listRestore,
                                highlightTenderId: t.id,
                              }}
                              className="text-yellow-700 hover:underline font-medium"
                            >
                              View
                            </Link>
                            {canEditTender && (
                              <button
                                type="button"
                                onClick={() => openEdit(t as Tender)}
                                className="text-gray-600 hover:text-yellow-700 inline-flex items-center gap-0.5"
                                title="Edit tender"
                                aria-label="Edit tender"
                              >
                                <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                                <span className="text-xs font-medium">Edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Call history */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {callLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(log.call_date, log.call_time)}
                      </span>
                      {log.called_by_name && (
                        <span className="text-xs text-gray-500">by {log.called_by_name}</span>
                      )}
                      {log.entity_id > 0 && (
                        <Link
                          to={`/tenders/${log.entity_id}`}
                          state={{ listRestore }}
                          className="text-xs text-yellow-700 hover:underline"
                        >
                          Tender #{log.entity_id}
                          {log.tender_awarder ? ` · ${log.tender_awarder}` : ''}
                          {log.tender_date ? ` · ${formatDate(log.tender_date)}` : ''}
                        </Link>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {log.call_status && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold border ${getSettingColorClass(log.call_status_color_code)}`}
                        >
                          {log.call_status}
                        </span>
                      )}
                      {log.call_outcome && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}
                        >
                          {log.call_outcome}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.notes && (
                    <div className="text-sm text-gray-700 mt-2 flex items-start space-x-2">
                      <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <p className="flex-1">{log.notes}</p>
                    </div>
                  )}
                  {log.next_call_date && (
                    <div className="mt-2 text-xs text-indigo-600 flex items-center space-x-1">
                      <Icon component={FiCalendar} className="w-3 h-3" />
                      <span>Next call: {formatDateTime(log.next_call_date)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {editModal}
      </div>
    </Layout>
  );
};

export default TenderAwardeeDetailPage;
